import type { SearchResultFragment } from '#graphql-operations'

/**
 * Discount math for a sale variant. `priceBeforeDiscount` is already
 * tax-inclusive — it sits on the same basis as `priceWithTax` (for a variant
 * that isn't discounted the two are exactly equal), so it is shown as-is and
 * compared against the gross price. Comparing it against the net `price`
 * instead makes the plain VAT markup look like a discount, which flags every
 * product in the catalog as on sale. Returns `null` when the variant isn't
 * actually discounted — callers can short-circuit on null.
 */
export function getSalePrice(variant: {
  priceBeforeDiscount?: number | null
  priceWithTax?: number | null
}): { gross: number, savedPercent: number } | null {
  const before = variant.priceBeforeDiscount
  const priceWithTax = variant.priceWithTax
  if (!before || !priceWithTax || before <= priceWithTax)
    return null
  return {
    gross: before,
    savedPercent: Math.round((before - priceWithTax) / before * 100),
  }
}

/**
 * Strike-through "old" price for a search-result tile, or `undefined` when the
 * product isn't discounted. `customProductVariantMappings.priceBeforeDiscount` is
 * already tax-inclusive, on the same basis as the `SearchResult` fragment's
 * `price` (aliased to `priceWithTax`), so the two are compared directly — see
 * {@link getSalePrice} for why scaling it would turn the plain VAT markup into a
 * phantom discount. `searchResultPrice` is the shared unwrapper from `useSeo`.
 */
export function searchResultOldPrice(product?: SearchResultFragment): number | undefined {
  const before = product?.customProductVariantMappings?.priceBeforeDiscount
  const current = searchResultPrice(product?.price)
  if (!before || !current || before <= current)
    return undefined
  return before
}

/**
 * Whether an order-level coupon has been prorated onto this line. Vendure
 * spreads an order discount across the lines, so `proratedLinePrice` drops
 * below the line's own (possibly already sale-) price when a code applies.
 */
export function orderLineHasCoupon(line: {
  linePrice: number
  discountedLinePrice?: number | null
  proratedLinePrice: number
}): boolean {
  return line.proratedLinePrice < (line.discountedLinePrice ?? line.linePrice)
}

/**
 * What the customer is actually charged for this line, coupons included — the
 * figure shown as *the* price wherever a line is listed. Shared so the cart
 * drawer, the checkout summary and the order confirmation cannot drift apart.
 */
export function orderLineChargedPrice(line: {
  linePrice: number
  discountedLinePrice?: number | null
  proratedLinePrice: number
}): number {
  return orderLineHasCoupon(line) ? line.proratedLinePrice : (line.discountedLinePrice ?? line.linePrice)
}

/**
 * Whether an order line is a gift-with-order line: priced at zero by the backend
 * and tagged with the rule that granted it. Its saved pre-gift value supplies
 * the displayed discount without changing the amount charged. Fixed gifts are locked;
 * a cheapest cart item claimed as a gift stays editable (`isEditableGiftOrderLine`).
 */
export function isGiftOrderLine(line: {
  customFields?: { giftRuleId?: string | null } | null
}): boolean {
  return !!line.customFields?.giftRuleId
}

/** The gift's saved selling value, including tax, before it was made free. */
export function orderLineGiftValue(line: {
  quantity: number
  giftOriginalUnitPriceWithTax?: number | null
  customFields?: { giftRuleId?: string | null } | null
}): number {
  const price = line.giftOriginalUnitPriceWithTax
  return isGiftOrderLine(line) && typeof price === 'number' && Number.isFinite(price) && price > 0
    ? price * line.quantity
    : 0
}

/**
 * What an order line would cost with no discount of any kind applied — the
 * figure the strike-through price and the order-level "you save" are both
 * measured against.
 *
 * It is deliberately independent of every discount Vendure applies: the
 * catalog before-price (`priceBeforeDiscount`, already tax-inclusive) scaled by
 * quantity, floored at the line's own listed `linePrice` for variants that
 * aren't on sale. `discountedLinePrice`/`proratedLinePrice` are never read here
 * — a coupon prorates onto the line and would otherwise pull the "original"
 * price down to the post-coupon amount, making the saving look smaller the more
 * the customer actually saved.
 *
 * Gifts use their saved pre-gift selling value as the floor. If an older gift
 * has no saved value, leave it out rather than infer a discount from today's catalogue.
 */
export function orderLineBasePrice(line: {
  quantity: number
  linePrice: number
  giftOriginalUnitPriceWithTax?: number | null
  productVariant: { priceBeforeDiscount?: number | null }
  customFields?: { giftRuleId?: string | null } | null
}): number {
  const giftValue = orderLineGiftValue(line)
  if (isGiftOrderLine(line) && giftValue === 0)
    return line.linePrice
  const before = (line.productVariant.priceBeforeDiscount ?? 0) * line.quantity
  return Math.max(before, line.linePrice, giftValue)
}
